import { useCallback } from "react";
import { fetchTransfers } from "@/lib/api";
import { useApiQuery } from "@/hooks/use-api";
import { transferStatusLabel, transferStatusVariant } from "@/features/transfers/transfer-state";
import type { WalletRecentTransfer } from "@/types";

const EMPTY_TRANSFERS: WalletRecentTransfer[] = [];
const RECENT_LIMIT = 5;

function boundedReasonCode(value: string): string {
  const code = value.trim().toUpperCase().replace(/\s+/g, "_");
  return /^[A-Z][A-Z0-9_]{0,63}$/.test(code) ? code : "TRANSFER_READ_FAILED";
}

function createdMillis(row: Record<string, unknown>): number {
  const parsed = Date.parse(String(row.createdAt ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

/** The gateway filters by wallet_id; check the returned source again before rendering. */
export function mapWalletRecentTransfers(rows: Record<string, unknown>[], walletId: string): WalletRecentTransfer[] {
  return rows
    .filter((row) => {
      const source = row.sourceWallet;
      return source && typeof source === "object" && !Array.isArray(source)
        && (source as Record<string, unknown>).id === walletId
        && typeof row.id === "string" && row.id.length > 0;
    })
    .sort((a, b) => createdMillis(b) - createdMillis(a))
    .slice(0, RECENT_LIMIT)
    .map((row) => {
      const status = String(row.status ?? "pending");
      const created = createdMillis(row);
      return {
        id: String(row.id),
        direction: "sent" as const,
        asset: String(row.asset ?? "—"),
        amount: String(row.amount ?? "—"),
        status: transferStatusLabel(status),
        statusVariant: transferStatusVariant(status),
        time: created ? new Date(created).toLocaleString() : "—",
      };
    });
}

export function useWalletRecentTransfers(walletId: string) {
  const fetcher = useCallback(async () => {
    try {
      return await fetchTransfers(walletId);
    } catch {
      // A browser fetch rejection has no HTTP response or gateway reason code.
      // Keep it distinct from an empty transfer list and make retry useful.
      return { transfers: [], reasonCode: "NETWORK_ERROR" };
    }
  }, [walletId]);
  const mapper = useCallback((response: Awaited<ReturnType<typeof fetchTransfers>>) => (
    response.reasonCode
      ? { data: EMPTY_TRANSFERS, error: { reasonCode: boundedReasonCode(response.reasonCode) } }
      : { data: mapWalletRecentTransfers(response.transfers, walletId) }
  ), [walletId]);
  const result = useApiQuery(fetcher, mapper, { initialData: EMPTY_TRANSFERS, enabled: Boolean(walletId) });
  return { transfers: result.data, loading: result.loading, error: result.error, refresh: result.refresh };
}
